import { useEffect, useLayoutEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';
import { getPreferredLanguage, languageOfPath } from '../utils/languagePreference';
import { documentNavigationType } from '../utils/navigation';

if ('scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}

// Per-history-entry memory of where the user was: scroll offset and the last
// link (or navigating button) they activated — in the header, the main content
// or the footer. Kept in sessionStorage so it survives a reload of the tab too.
const STORE_KEY = 'ukwa-nav-memory';

function readMemory() {
  try {
    return JSON.parse(sessionStorage.getItem(STORE_KEY)) || {};
  } catch {
    return {};
  }
}

const MAX_ENTRIES = 50;

function writeMemory(memory) {
  // Keep only the most recent entries (insertion order) so it can't grow unbounded.
  const keys = Object.keys(memory);
  for (const k of keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES))) delete memory[k];
  try {
    sessionStorage.setItem(STORE_KEY, JSON.stringify(memory));
  } catch {
    // storage unavailable — restoration just won't happen
  }
}

// Where a remembered control lives, so Back returns focus to the same one (the
// header renders desktop and mobile variants of some links, and the same href
// can be in the header, the page and the footer).
const REGIONS = { header: 'header', main: '#main-content', footer: 'footer' };

// What to remember about an activated element: links by href; buttons only if
// they navigate and say so with data-nav-id (e.g. the language toggle).
function describeActivated(target) {
  const el = target.closest?.('a[href], [data-nav-id]');
  if (!el) return null;
  const region = Object.keys(REGIONS).find((r) => el.closest(REGIONS[r]));
  if (!region) return null;
  const navId = el.getAttribute('data-nav-id');
  const sel = navId
    ? `[data-nav-id="${CSS.escape(navId)}"]`
    : `a[href="${CSS.escape(el.getAttribute('href'))}"]`;
  return { region, sel };
}

// The remembered element, visible, in its region. Entries saved before
// regions were remembered hold only a main-content href.
function findRemembered(saved) {
  const region = saved.region || (saved.href ? 'main' : null);
  const sel = saved.sel || (saved.href ? `a[href="${CSS.escape(saved.href)}"]` : null);
  if (!region || !sel) return null;
  const root = document.querySelector(REGIONS[region]);
  return [...(root?.querySelectorAll(sel) || [])].find((el) => el.getClientRects().length > 0) || null;
}

// A remembered link on a page that is now shown in another language (see
// LanguageOverHistory): the same link, with the locale segment of its href
// swapped. Selectors hold CSS.escape()d hrefs, e.g. a[href="\/en\/about"].
function inLanguage(saved, lang) {
  if (!saved.sel || !lang) return saved;
  return {
    ...saved,
    sel: saved.sel.replace(/(href=")(\\?\/)(en|cy|gd)(?=\\?\/|\\?\?|")/, `$1$2${lang}`),
  };
}

// Jump, don't animate: <html> has scroll-behavior: smooth, and a new page
// sliding up from the previous page's scroll position is disorienting. An
// explicit behavior overrides the stylesheet; an inline scroll-behavior on
// <html> did not reliably do so.
function scrollToY(y) {
  window.scrollTo({ top: y, left: 0, behavior: 'instant' });
}

function isShown(el) {
  return !!el && el.getClientRects().length > 0;
}

// When a new page loads — the first load of the site and every in-app
// navigation — focus goes to the start of the page, just before "Skip to main
// content": the link stays hidden, so mouse users never see it pop up, and the
// first Tab shows it and screen readers announce it. Activating it focuses
// <main>, so on a first-level collection the next Tab reaches "Back to all
// themes", the first control in it (bug #55; WCAG 2.4.1, 2.4.3).
function focusNewPage() {
  document.getElementById('page-start')?.focus({ preventScroll: true });
}

// Route-change scroll + focus management (WCAG 2.4.3 Focus Order):
//   • First load, PUSH / REPLACE (a new page): scroll to the top and move
//     focus to the start of the page, just before "Skip to main content"
//     (see focusNewPage), so the first Tab reaches that link.
//   • POP (browser Back / Forward, Alt+←, "go back" voice command): restore
//     the scroll position and put focus back on the control the user activated
//     on that page — a collection card, a header or footer link, the language
//     toggle — instead of the top (RRO bugs #41, #48; OA bug #46).
export default function ScrollToTop() {
  const location = useLocation();
  const navType = useNavigationType();
  const { key, pathname } = location;
  // Set when LanguageOverHistory replaced a Back / Forward entry with the same
  // page in the chosen language: restore that entry's scroll and focus here.
  const restoreKey = navType === 'REPLACE' ? location.state?.restoreKey : undefined;
  const restoreLang = restoreKey ? location.state?.restoreLang : undefined;
  const isFirstRender = useRef(true);
  const prevPathname = useRef(pathname);

  // Track the scroll offset synchronously, so it can be saved for the entry we
  // are leaving before the new page resets it.
  const lastY = useRef(typeof window === 'undefined' ? 0 : window.scrollY);
  const prevKey = useRef(key);
  useEffect(() => {
    const onScroll = () => { lastY.current = window.scrollY; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Go back into the site from another document — the website calendar (the
  // archive player), another site — with Alt+← or the Back button. When the
  // browser restores the page from its back/forward cache, the element that
  // had focus still has it, but no focus event fires: screen readers announce
  // nothing (the card's name and details), and the first Tab only brings
  // keyboard focus back into the page, so focus moves on the second Tab
  // (OA / RRO, WATech RRO P3). Focus the remembered link — else the element
  // that had focus — again, so it is announced and the next Tab moves on from
  // it. (A page shown again in another language is handled by
  // LanguageOverHistory and the restoreKey path below.)
  const keyRef = useRef(key);
  keyRef.current = key;
  useEffect(() => {
    const onPageShow = (e) => {
      if (!e.persisted) return;
      const urlLang = languageOfPath(window.location.pathname);
      const preferred = getPreferredLanguage();
      if (urlLang && preferred && urlLang !== preferred) return;
      const active = document.activeElement;
      const target =
        findRemembered(readMemory()[keyRef.current] || {}) ||
        (active && active !== document.body ? active : null);
      if (!target) return;
      target.blur?.();
      requestAnimationFrame(() => target.focus({ preventScroll: true }));
    };
    window.addEventListener('pageshow', onPageShow);
    return () => window.removeEventListener('pageshow', onPageShow);
  }, []);

  // Remember the last link / navigating button activated for the current entry.
  useEffect(() => {
    const onActivate = (e) => {
      const found = describeActivated(e.target);
      if (!found) return;
      lastY.current = window.scrollY;
      const memory = readMemory();
      const { href, ...rest } = memory[key] || {};
      memory[key] = { ...rest, ...found, y: window.scrollY };
      writeMemory(memory);
    };
    document.addEventListener('click', onActivate, true);
    return () => document.removeEventListener('click', onActivate, true);
  }, [key]);

  useLayoutEffect(() => {
    const firstRender = isFirstRender.current;
    isFirstRender.current = false;

    // (Not for the entry just replaced by LanguageOverHistory: it was only on
    // screen for a moment, and its saved position is the one to restore.)
    if (prevKey.current !== key && prevKey.current !== restoreKey) {
      const memory = readMemory();
      memory[prevKey.current] = { ...memory[prevKey.current], y: lastY.current };
      writeMemory(memory);
    }
    prevKey.current = key;

    // Same page, new query (e.g. ?page=3 from the pagination): not a new page,
    // so no scroll-to-top and no focus move — the page's own handler deals with
    // scrolling and focus. Back / Forward between those entries (POP) still
    // restores the scroll position and the pagination button that was pressed.
    const samePage = navType !== 'POP' && prevPathname.current === pathname;
    prevPathname.current = pathname;
    if (samePage && !firstRender) return undefined;

    // A document loaded by Back / Forward (not restored from the cache) is a
    // return to a page, not a new one: restore its scroll and focus below.
    const backIntoSite = firstRender && documentNavigationType() === 'back_forward';
    if ((navType !== 'POP' && !restoreKey) || (firstRender && !backIntoSite)) {
      scrollToY(0);
      // The cookie banner, if it opens on the first load, takes focus from
      // the start of the page and gives it back when it closes.
      return focusNewPage();
    }

    // POP: content may still be loading (collections data, markdown), so
    // retry briefly until the remembered link exists and the page is tall
    // enough to scroll back to where it was.
    const saved = restoreKey
      ? inLanguage(readMemory()[restoreKey] || {}, pathname.split('/')[1])
      : readMemory()[key] || {};
    let frame;
    let tries = 0;
    // Go back after choosing another language: the page is shown again in the
    // new language, so focus goes to the language button the user just used —
    // the one for the page's previous language, i.e. the button that switches
    // back — not to a link on the page.
    const languageButton = () => {
      const buttons = [...document.querySelectorAll('header .lang-btn')].filter(isShown);
      return buttons.find((b) => b.getAttribute('lang') === restoreLang) || buttons[0] || null;
    };
    const restore = () => {
      tries += 1;
      const remembered = !restoreKey && !!(saved.sel || saved.href);
      const target = restoreKey ? languageButton() : remembered ? findRemembered(saved) : null;
      const tallEnough =
        saved.y == null ||
        document.documentElement.scrollHeight - window.innerHeight >= saved.y;
      if (((target || !remembered) && tallEnough) || tries >= 60) {
        scrollToY(saved.y ?? 0);
        // Nothing to return to on a page loaded by Back: start at the top,
        // like any page load.
        const fallback = backIntoSite ? document.getElementById('page-start') : null;
        (target || fallback)?.focus({ preventScroll: true });
        return;
      }
      frame = requestAnimationFrame(restore);
    };
    restore();
    return () => cancelAnimationFrame(frame);
  }, [key, navType, pathname, restoreKey, restoreLang]);

  return null;
}
